import React from 'react';
import { Plus } from 'lucide-react';

/*  ─── SVG illustrations ───
 *  Minimal, line-based illustrations that work in both light and dark mode.
 *  Each illustration uses currentColor so it inherits the text colour from its
 *  parent, plus brand-600 as the accent via className utilities.
 *  All viewBoxes are 200×160 to keep a consistent aspect ratio.
 */

function FormsIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Form card */}
      <rect x="50" y="20" width="100" height="120" rx="8" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
      {/* Header bar */}
      <rect x="62" y="36" width="76" height="6" rx="3" className="fill-brand-500/20" />
      {/* Input fields */}
      <rect x="62" y="54" width="76" height="10" rx="3" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1" />
      <rect x="62" y="72" width="76" height="10" rx="3" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1" />
      <rect x="62" y="90" width="50" height="10" rx="3" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1" />
      {/* Submit button */}
      <rect x="62" y="112" width="36" height="12" rx="4" className="fill-brand-500" />
      {/* Decorative checkbox */}
      <rect x="116" y="91" width="8" height="8" rx="2" className="fill-brand-500/30" />
      {/* Checkmark */}
      <path d="M118 95.5L119.5 97L122 93.5" className="stroke-brand-600" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Corner decoration */}
      <circle cx="160" cy="28" r="6" className="fill-brand-100 dark:fill-brand-900/30" />
      <circle cx="40" cy="132" r="4" className="fill-gray-100 dark:fill-gray-800" />
    </svg>
  );
}

function PagesIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Browser frame */}
      <rect x="35" y="20" width="130" height="120" rx="8" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
      {/* Browser top bar */}
      <rect x="35" y="20" width="130" height="20" rx="8" className="fill-gray-50 dark:fill-gray-800" />
      <rect x="35" y="32" width="130" height="8" className="fill-gray-50 dark:fill-gray-800" />
      {/* Browser dots */}
      <circle cx="48" cy="30" r="2.5" className="fill-gray-300 dark:fill-gray-600" />
      <circle cx="57" cy="30" r="2.5" className="fill-gray-300 dark:fill-gray-600" />
      <circle cx="66" cy="30" r="2.5" className="fill-gray-300 dark:fill-gray-600" />
      {/* URL bar */}
      <rect x="78" y="26" width="76" height="8" rx="4" className="fill-white dark:fill-gray-700 stroke-gray-200 dark:stroke-gray-600" strokeWidth="0.75" />
      {/* Hero section */}
      <rect x="55" y="52" width="90" height="6" rx="3" className="fill-brand-500/20" />
      <rect x="65" y="64" width="70" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="75" y="72" width="50" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      {/* CTA button on page */}
      <rect x="82" y="84" width="36" height="10" rx="4" className="fill-brand-500" />
      {/* Content blocks */}
      <rect x="48" y="104" width="30" height="22" rx="4" className="fill-gray-100 dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
      <rect x="85" y="104" width="30" height="22" rx="4" className="fill-gray-100 dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
      <rect x="122" y="104" width="30" height="22" rx="4" className="fill-gray-100 dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
    </svg>
  );
}

function SitesIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Globe */}
      <circle cx="100" cy="75" r="40" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
      {/* Horizontal lines */}
      <ellipse cx="100" cy="75" rx="40" ry="15" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      <ellipse cx="100" cy="75" rx="28" ry="40" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      {/* Vertical center line */}
      <line x1="100" y1="35" x2="100" y2="115" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      {/* Horizontal center */}
      <line x1="60" y1="75" x2="140" y2="75" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      {/* Accent marker */}
      <circle cx="120" cy="58" r="6" className="fill-brand-500/20 stroke-brand-500" strokeWidth="1.2" />
      <circle cx="120" cy="58" r="2" className="fill-brand-500" />
      {/* Connection line */}
      <line x1="126" y1="56" x2="152" y2="42" className="stroke-brand-500/40" strokeWidth="1" strokeDasharray="3 2" />
      {/* Floating card */}
      <rect x="142" y="30" width="36" height="24" rx="4" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      <rect x="148" y="37" width="24" height="3" rx="1.5" className="fill-brand-500/30" />
      <rect x="148" y="43" width="16" height="3" rx="1.5" className="fill-gray-200 dark:fill-gray-700" />
      {/* Bottom decoration */}
      <circle cx="55" cy="120" r="3" className="fill-brand-100 dark:fill-brand-900/30" />
      <circle cx="145" cy="125" r="2" className="fill-gray-200 dark:fill-gray-700" />
    </svg>
  );
}

function WorkflowsIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Node 1 - trigger */}
      <rect x="30" y="30" width="52" height="28" rx="6" className="fill-brand-50 dark:fill-brand-900/20 stroke-brand-300 dark:stroke-brand-700" strokeWidth="1.2" />
      <rect x="40" y="39" width="32" height="4" rx="2" className="fill-brand-400/50" />
      <rect x="40" y="47" width="20" height="3" rx="1.5" className="fill-brand-300/30" />
      {/* Arrow 1 */}
      <path d="M82 44L100 44" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M96 40L100 44L96 48" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Node 2 - action */}
      <rect x="102" y="30" width="52" height="28" rx="6" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.2" />
      <rect x="112" y="39" width="32" height="4" rx="2" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="112" y="47" width="20" height="3" rx="1.5" className="fill-gray-200 dark:fill-gray-700" />
      {/* Branch split */}
      <path d="M128 58L128 74" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M128 74L80 92" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M128 74L168 92" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1.2" strokeLinecap="round" />
      {/* Decision diamond */}
      <path d="M128 68L136 74L128 80L120 74Z" className="fill-amber-100 dark:fill-amber-900/20 stroke-amber-400 dark:stroke-amber-600" strokeWidth="1" />
      {/* Node 3a */}
      <rect x="52" y="94" width="52" height="28" rx="6" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.2" />
      <rect x="62" y="103" width="32" height="4" rx="2" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="62" y="111" width="20" height="3" rx="1.5" className="fill-gray-200 dark:fill-gray-700" />
      {/* Node 3b */}
      <rect x="142" y="94" width="52" height="28" rx="6" className="fill-emerald-50 dark:fill-emerald-900/20 stroke-emerald-300 dark:stroke-emerald-700" strokeWidth="1.2" />
      <rect x="152" y="103" width="32" height="4" rx="2" className="fill-emerald-400/50" />
      <rect x="152" y="111" width="20" height="3" rx="1.5" className="fill-emerald-300/30" />
      {/* Decorative dots */}
      <circle cx="20" cy="74" r="2.5" className="fill-gray-100 dark:fill-gray-800" />
      <circle cx="190" cy="74" r="2.5" className="fill-brand-100 dark:fill-brand-900/30" />
    </svg>
  );
}

function ChatbotsIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Chat window */}
      <rect x="45" y="16" width="110" height="128" rx="8" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
      {/* Window header */}
      <rect x="45" y="16" width="110" height="22" rx="8" className="fill-gray-50 dark:fill-gray-800" />
      <rect x="45" y="30" width="110" height="8" className="fill-gray-50 dark:fill-gray-800" />
      {/* Bot avatar in header */}
      <circle cx="60" cy="27" r="5" className="fill-brand-100 dark:fill-brand-900/30" />
      <circle cx="60" cy="26" r="1.5" className="fill-brand-500" />
      <path d="M57 29C57 27.8 58.3 27 60 27C61.7 27 63 27.8 63 29" className="stroke-brand-500" strokeWidth="0.8" strokeLinecap="round" />
      {/* Online indicator */}
      <circle cx="64" cy="31" r="1.5" className="fill-emerald-400" />
      {/* Bot name */}
      <rect x="70" y="24" width="30" height="3" rx="1.5" className="fill-gray-400 dark:fill-gray-500" />
      <rect x="70" y="29" width="18" height="2" rx="1" className="fill-gray-200 dark:fill-gray-700" />
      {/* Bot message 1 */}
      <rect x="55" y="48" width="68" height="20" rx="6" className="fill-gray-100 dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
      <rect x="62" y="54" width="46" height="3" rx="1.5" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="62" y="60" width="30" height="3" rx="1.5" className="fill-gray-300 dark:fill-gray-600" />
      {/* User message */}
      <rect x="90" y="76" width="55" height="16" rx="6" className="fill-brand-500" />
      <rect x="98" y="82" width="36" height="3" rx="1.5" className="fill-white/60" />
      {/* Bot message 2 */}
      <rect x="55" y="100" width="58" height="20" rx="6" className="fill-gray-100 dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
      <rect x="62" y="106" width="40" height="3" rx="1.5" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="62" y="112" width="24" height="3" rx="1.5" className="fill-gray-300 dark:fill-gray-600" />
      {/* Input area */}
      <rect x="55" y="128" width="90" height="8" rx="4" className="stroke-gray-200 dark:stroke-gray-600" strokeWidth="0.75" />
      {/* Typing indicator dots */}
      <circle cx="66" cy="132" r="1.2" className="fill-gray-300 dark:fill-gray-600" />
      <circle cx="71" cy="132" r="1.2" className="fill-gray-300 dark:fill-gray-600" />
      <circle cx="76" cy="132" r="1.2" className="fill-gray-300 dark:fill-gray-600" />
    </svg>
  );
}

function CRMIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Table card */}
      <rect x="30" y="24" width="140" height="112" rx="8" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
      {/* Table header */}
      <rect x="30" y="24" width="140" height="22" rx="8" className="fill-gray-50 dark:fill-gray-800" />
      <rect x="30" y="38" width="140" height="8" className="fill-gray-50 dark:fill-gray-800" />
      {/* Header text */}
      <rect x="42" y="32" width="28" height="4" rx="2" className="fill-gray-400 dark:fill-gray-500" />
      <rect x="90" y="32" width="22" height="4" rx="2" className="fill-gray-400 dark:fill-gray-500" />
      <rect x="132" y="32" width="26" height="4" rx="2" className="fill-gray-400 dark:fill-gray-500" />
      {/* Row 1 */}
      <line x1="30" y1="46" x2="170" y2="46" className="stroke-gray-100 dark:stroke-gray-800" strokeWidth="1" />
      <circle cx="46" cy="58" r="5" className="fill-brand-100 dark:fill-brand-900/30" />
      <rect x="56" y="55" width="24" height="4" rx="2" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="90" y="55" width="18" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="132" y="55" width="30" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      {/* Row 2 */}
      <line x1="30" y1="70" x2="170" y2="70" className="stroke-gray-100 dark:stroke-gray-800" strokeWidth="1" />
      <circle cx="46" cy="82" r="5" className="fill-emerald-100 dark:fill-emerald-900/30" />
      <rect x="56" y="79" width="28" height="4" rx="2" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="90" y="79" width="22" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="132" y="79" width="26" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      {/* Row 3 */}
      <line x1="30" y1="94" x2="170" y2="94" className="stroke-gray-100 dark:stroke-gray-800" strokeWidth="1" />
      <circle cx="46" cy="106" r="5" className="fill-amber-100 dark:fill-amber-900/30" />
      <rect x="56" y="103" width="20" height="4" rx="2" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="90" y="103" width="16" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="132" y="103" width="22" height="4" rx="2" className="fill-gray-200 dark:fill-gray-700" />
      {/* Row divider */}
      <line x1="30" y1="118" x2="170" y2="118" className="stroke-gray-100 dark:stroke-gray-800" strokeWidth="1" />
      {/* Add row indicator */}
      <circle cx="46" cy="128" r="4" className="stroke-brand-400 stroke-dashed" strokeWidth="1" strokeDasharray="2 2" />
      <line x1="44" y1="128" x2="48" y2="128" className="stroke-brand-400" strokeWidth="1" strokeLinecap="round" />
      <line x1="46" y1="126" x2="46" y2="130" className="stroke-brand-400" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function ResponsesIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Inbox/collection metaphor */}
      <rect x="55" y="30" width="90" height="100" rx="8" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1.5" />
      {/* Empty state lines in the inbox */}
      <rect x="70" y="50" width="60" height="4" rx="2" className="fill-gray-100 dark:fill-gray-800" />
      <rect x="70" y="60" width="48" height="4" rx="2" className="fill-gray-100 dark:fill-gray-800" />
      <rect x="70" y="70" width="54" height="4" rx="2" className="fill-gray-100 dark:fill-gray-800" />
      {/* Empty inbox symbol */}
      <path d="M88 90L100 102L112 90" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="100" y1="84" x2="100" y2="102" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1.5" strokeLinecap="round" />
      {/* Floating form submission cards */}
      <rect x="26" y="40" width="26" height="18" rx="4" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" opacity="0.7" />
      <rect x="31" y="45" width="16" height="2.5" rx="1.2" className="fill-brand-400/30" />
      <rect x="31" y="50" width="10" height="2.5" rx="1.2" className="fill-gray-200 dark:fill-gray-700" />
      <rect x="148" y="50" width="26" height="18" rx="4" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" opacity="0.7" />
      <rect x="153" y="55" width="16" height="2.5" rx="1.2" className="fill-brand-400/30" />
      <rect x="153" y="60" width="10" height="2.5" rx="1.2" className="fill-gray-200 dark:fill-gray-700" />
      {/* Share/link icon */}
      <circle cx="100" cy="114" r="5" className="fill-brand-50 dark:fill-brand-900/20" />
      <path d="M97 114L103 114M101 112L103 114L101 116" className="stroke-brand-500" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PipelineIllustration() {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Kanban columns */}
      {/* Column 1 */}
      <rect x="18" y="24" width="50" height="112" rx="6" className="fill-gray-50 dark:fill-gray-800/50 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      <rect x="26" y="32" width="28" height="4" rx="2" className="fill-gray-400 dark:fill-gray-500" />
      {/* Cards in column 1 */}
      <rect x="26" y="44" width="34" height="22" rx="4" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
      <rect x="31" y="50" width="22" height="3" rx="1.5" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="31" y="56" width="14" height="2.5" rx="1.2" className="fill-brand-400/30" />
      <rect x="26" y="72" width="34" height="22" rx="4" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
      <rect x="31" y="78" width="18" height="3" rx="1.5" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="31" y="84" width="12" height="2.5" rx="1.2" className="fill-emerald-400/30" />
      {/* Column 2 */}
      <rect x="75" y="24" width="50" height="112" rx="6" className="fill-gray-50 dark:fill-gray-800/50 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      <rect x="83" y="32" width="32" height="4" rx="2" className="fill-amber-400 dark:fill-amber-500" />
      {/* Card in column 2 */}
      <rect x="83" y="44" width="34" height="22" rx="4" className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.75" />
      <rect x="88" y="50" width="24" height="3" rx="1.5" className="fill-gray-300 dark:fill-gray-600" />
      <rect x="88" y="56" width="16" height="2.5" rx="1.2" className="fill-amber-400/30" />
      {/* Column 3 */}
      <rect x="132" y="24" width="50" height="112" rx="6" className="fill-gray-50 dark:fill-gray-800/50 stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
      <rect x="140" y="32" width="24" height="4" rx="2" className="fill-emerald-400 dark:fill-emerald-500" />
      {/* Empty column indicator */}
      <rect x="140" y="44" width="34" height="22" rx="4" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" strokeDasharray="4 3" />
      <line x1="153" y1="52" x2="161" y2="52" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1" strokeLinecap="round" />
      <line x1="157" y1="48" x2="157" y2="56" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="1" strokeLinecap="round" />
      {/* Moving card arrow */}
      <path d="M64 55L72 55" className="stroke-brand-400" strokeWidth="1" strokeLinecap="round" />
      <path d="M70 53L72 55L70 57" className="stroke-brand-400" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const illustrations = {
  forms: FormsIllustration,
  pages: PagesIllustration,
  sites: SitesIllustration,
  workflows: WorkflowsIllustration,
  chatbots: ChatbotsIllustration,
  crm: CRMIllustration,
  responses: ResponsesIllustration,
  pipeline: PipelineIllustration,
};

/**
 * Reusable illustrated empty state component.
 *
 * @param {string}     illustration   - Key from the illustrations map (forms, pages, sites, workflows, chatbots, crm, responses, pipeline)
 * @param {string}     headline       - Primary headline text
 * @param {string}     description    - Supporting description text
 * @param {string}     [buttonLabel]  - CTA button label (omit to hide button)
 * @param {function}   [onAction]     - CTA button click handler
 * @param {ReactNode}  [customAction] - Custom action element (replaces default button)
 * @param {string}     [className]    - Additional wrapper classes
 */
export default function EmptyState({
  illustration,
  headline,
  description,
  buttonLabel,
  onAction,
  customAction,
  className = '',
}) {
  const Illustration = illustrations[illustration];

  return (
    <div className={`flex flex-col items-center justify-center py-16 ${className}`}>
      {/* Illustration */}
      {Illustration && (
        <div className="w-48 h-[154px] mb-6">
          <Illustration />
        </div>
      )}

      {/* Text */}
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
        {headline}
      </h3>
      <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto text-center leading-relaxed">
        {description}
      </p>

      {/* Action */}
      {customAction ? (
        <div className="mt-6">{customAction}</div>
      ) : buttonLabel && onAction ? (
        <button
          onClick={onAction}
          className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" />
          {buttonLabel}
        </button>
      ) : null}
    </div>
  );
}
